import i18next from 'i18next';
import React, {useState} from 'react';

import {Button} from '@famly/mf_input_button';
import {Select, type SelectOption} from '@famly/mf_input_select';
import {Icon} from '@famly/mf_data-display_icon';
import {FormControl} from '@famly/mf_input_form-control';
import {Text} from '@famly/mf_data-display_text';
import {Stack} from '@famly/mf_layout_stack';
import {Box} from '@famly/mf_layout_box';
import {FormLabel} from '@famly/mf_input_form-label';
import Tooltip from 'web-app/react/components/tooltip/tooltip';

import type {HandlePlanStartDateChange, HandlePlanEndDateChange} from '../../types';

type Props = {
    startDate: {
        value: string;
        formattedDate: string;
        options: Array<SelectOption>;
        onChange: HandlePlanStartDateChange;
        isDisabled: boolean;
    };
    endDate: {
        value: string | null;
        formattedDate: string | null;
        options: Array<SelectOption>;
        onChange: HandlePlanEndDateChange;
        isDisabled: boolean;
    };
};

export const PlanStartAndEndDateBanner = ({startDate, endDate}: Props) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const {isDisabled} = endDate;

    return (
        <Stack
            direction="column"
            backgroundColor="b100"
            borderRadius={2}
            px={4}
            py={3}
            spacing={4}
            data-e2e-id="plan-start-and-end-date-banner"
        >
            <Stack alignItems="center" justifyContent="space-between">
                <Stack alignItems="center" spacing={2}>
                    <Icon name="schedule" color="b400" />
                    <Stack spacing={1}>
                        <Text variant="body">
                            {isExpanded
                                ? i18next.t(
                                      'react.simplifiedUsBillingPlan.translations.json.effectiveChangeDateBanner.takesEffect',
                                  )
                                : i18next.t(
                                      'react.simplifiedUsBillingPlan.translations.json.effectiveChangeDateBanner.takesEffectFrom',
                                  )}
                        </Text>
                        {!isExpanded && (
                            <Stack spacing={1}>
                                <Text variant="body" emphasized>
                                    {startDate.formattedDate}
                                </Text>
                                {endDate.value && (
                                    <>
                                        <Text variant="body">
                                            {i18next
                                                .t(
                                                    'react.simplifiedUsBillingPlan.translations.json.effectiveChangeDateBanner.to',
                                                )
                                                .toLowerCase()}
                                        </Text>
                                        <Text variant="body" emphasized data-e2e-id="plan-end-date">
                                            {endDate.formattedDate}
                                        </Text>
                                    </>
                                )}
                            </Stack>
                        )}
                    </Stack>
                </Stack>
                <Box onClick={() => setIsExpanded(!isExpanded)} data-e2e-id="toggle-date-range-banner">
                    <Text variant="body" color="b400" cursor="pointer">
                        {isExpanded
                            ? i18next.t(
                                  'react.simplifiedUsBillingPlan.translations.json.effectiveChangeDateBanner.close',
                              )
                            : i18next.t(
                                  'react.simplifiedUsBillingPlan.translations.json.effectiveChangeDateBanner.change',
                              )}
                    </Text>
                </Box>
            </Stack>
            {isExpanded && (
                <Stack justifyContent="space-between" spacing={4} alignItems="center">
                    <FormControl
                        label={i18next.t(
                            'react.simplifiedUsBillingPlan.translations.json.effectiveChangeDateBanner.from',
                        )}
                        fullWidth
                    >
                        <Select
                            fullWidth
                            options={startDate.options}
                            value={startDate.value}
                            onChange={startDate.onChange}
                            disabled={startDate.isDisabled}
                            data-e2e-id="select-start-date"
                        />
                    </FormControl>
                    <Box alignSelf="flex-end" pb={3}>
                        <Text variant="body">-</Text>
                    </Box>
                    <FormControl fullWidth>
                        <FormLabel>
                            <Stack alignItems="center" spacing={1} mb={1}>
                                {i18next.t(
                                    'react.simplifiedUsBillingPlan.translations.json.effectiveChangeDateBanner.to',
                                )}
                                {isDisabled && (
                                    <Tooltip
                                        trigger={['hover']}
                                        placement="top"
                                        overlay={i18next.t(
                                            'react.simplifiedUsBillingPlan.translations.json.effectiveChangeDateBanner.endDateTooltip',
                                        )}
                                    >
                                        <Stack alignContent="center">
                                            <Icon size={16} name="info" color="n300" />
                                        </Stack>
                                    </Tooltip>
                                )}
                            </Stack>
                        </FormLabel>
                        <Stack alignItems="center">
                            <Select
                                fullWidth
                                options={endDate.options}
                                value={endDate.value}
                                onChange={endDate.onChange}
                                disabled={isDisabled}
                                data-e2e-id="select-end-date"
                            />
                            {endDate.value && !isDisabled && (
                                <Button
                                    variant="transparent"
                                    icon="delete"
                                    aria-label={i18next.t(
                                        'react.simplifiedUsBillingPlan.translations.json.effectiveChangeDateBanner.clearEndDate',
                                    )}
                                    onClick={() => endDate.onChange(null)}
                                />
                            )}
                        </Stack>
                    </FormControl>
                </Stack>
            )}
        </Stack>
    );
};
